import { useEffect, useState, useMemo } from "react";
import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import { useLoading } from "../../../../../shared/context/LoadingContext";
import moment from "moment";
import toast from "react-hot-toast";
import XLSX from "xlsx-js-style";
import "moment/locale/pt";
import "./RelatorioProcedimentosMedico.scss";
import { Link } from "react-router-dom";

moment.locale("pt");

function RelatorioProcedimentosMedico({
  dataFiltro,
  dadosClinica,
  auth,
  acao,
  atualizacoes,
}) {
  const { startLoading, stopLoading } = useLoading();
  const [dados, setDados] = useState(null);
  const [error, setError] = useState(null);
  const { sendRequest } = useHttpClient();
  const [showFiltros, setShowFiltros] = useState(true);

  // Estados para filtros
  const [filtros, setFiltros] = useState({
    medicos: [],
    tipoExecucao: [],
    pesquisa: "",
  });

  // Estados para paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Estados para dados auxiliares
  const [medicos, setMedicos] = useState([]);

  // Tipos de execução disponíveis
  const tiposExecucao = [
    {
      value: "SESSAO_PLANO_TRATAMENTO",
      label: "Sessão de Plano",
      color: "primary",
    },
    {
      value: "TRATAMENTO_ISOLADO",
      label: "Tratamento Isolado",
      color: "secondary",
    },
    {
      value: "EVOLUCAO_AGENDAMENTO_TRANSICAO",
      label: "Evolução",
      color: "warning",
    },
  ];

  // Carregar médicos
  useEffect(() => {
    const carregarMedicos = async () => {
      try {
        const headers = { Authorization: "Bearer " + auth.token };
        const resMedicos = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/users/dentistas/${dadosClinica._id}`,
          "GET",
          null,
          headers
        );
        setMedicos(resMedicos.dentistas || []);
      } catch (err) {
        console.error("Erro ao carregar médicos:", err);
      }
    };
    carregarMedicos();
  }, [dadosClinica._id, sendRequest, auth.token]);

  // Executar ação quando solicitado
  useEffect(() => {
    if (acao === "gerar") {
      gerarRelatorio();
    } else if (acao === "exportar" && dados) {
      exportarRelatorio();
    }
  }, [acao, atualizacoes]);

  const gerarRelatorio = async () => {
    setError(null);
    startLoading();
    try {
      // Construir URL com query params
      const params = new URLSearchParams({
        dataInicio: dataFiltro.dataInicial.format("YYYY-MM-DD"),
        dataFim: dataFiltro.dataFinal.format("YYYY-MM-DD"),
      });

      // Adicionar médicos se filtrados
      if (filtros.medicos.length > 0) {
        params.append("medicos", filtros.medicos.join(","));
      }

      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/planostratamento/relatorio-procedimentos-medico/${dadosClinica._id}?${params}`,
        "GET",
        null,
        {
          Authorization: "Bearer " + auth.token,
        }
      );

      setDados(response.data);
    } catch (err) {
      setError(err.message || "Erro ao gerar relatório");
      toast.error("Erro ao gerar relatório");
    } finally {
      stopLoading();
    }
  };

  const exportarRelatorio = async () => {
    if (!dados) {
      toast.error("É necessário gerar o relatório primeiro.");
      return;
    }

    startLoading();
    try {
      const wb = XLSX.utils.book_new();

      // Aba principal - Procedimentos
      const wsData = [
        [
          "Médico",
          "Tipo de Execução",
          "Cliente",
          "Tratamento",
          "Dente",
          "Faces",
          "Data Início",
          "Data Fim",
          "Observações/Evolução",
          "Origem",
        ],
      ];

      // Processar dados dependendo do formato
      const procedimentos = Array.isArray(dados)
        ? dados.flatMap((m) => m.procedimentos)
        : dados.procedimentos || [];

      procedimentos.forEach((p) => {
        wsData.push([
          p.medico?.name || "N/A",
          getTipoExecucaoLabel(p.tipo_execucao),
          p.cliente?.nome || "N/A",
          p.tratamento?.designacao || "N/A",
          p.dente || "N/A",
          p.faces?.join(", ") || "N/A",
          p.data_inicio
            ? moment(p.data_inicio).format("DD/MM/YYYY HH:mm")
            : "N/A",
          p.data_fim ? moment(p.data_fim).format("DD/MM/YYYY HH:mm") : "N/A",
          p.evolucao || p.relatorio || "N/A",
          p.numero_plano || p.numero || "N/A",
        ]);
      });

      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Estilizar cabeçalho
      const headerStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "366092" } },
        alignment: { horizontal: "center" },
      };

      for (let i = 0; i < 10; i++) {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: i });
        if (!ws[cellRef]) continue;
        ws[cellRef].s = headerStyle;
      }

      // Ajustar largura das colunas
      ws["!cols"] = [
        { wch: 25 }, // Médico
        { wch: 20 }, // Tipo
        { wch: 30 }, // Cliente
        { wch: 30 }, // Tratamento
        { wch: 10 }, // Dente
        { wch: 15 }, // Faces
        { wch: 18 }, // Data Início
        { wch: 18 }, // Data Fim
        { wch: 40 }, // Observações
        { wch: 15 }, // Origem
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Procedimentos");

      // Aba de Resumo
      const resumoData = [
        ["RESUMO DO RELATÓRIO"],
        [],
        [
          "Período:",
          `${dataFiltro.dataInicial.format(
            "DD/MM/YYYY"
          )} a ${dataFiltro.dataFinal.format("DD/MM/YYYY")}`,
        ],
        ["Total de Procedimentos:", procedimentos.length],
        [],
        ["RESUMO POR TIPO"],
      ];

      // Calcular resumo
      const resumo = calcularResumo(procedimentos);
      Object.entries(resumo.por_tipo).forEach(([tipo, qtd]) => {
        resumoData.push([getTipoExecucaoLabel(tipo) + ":", qtd]);
      });

      resumoData.push([], ["RESUMO POR MÉDICO"]);

      if (Array.isArray(dados)) {
        dados.forEach((medicoData) => {
          resumoData.push([
            medicoData.medico?.name || "N/A",
            medicoData.totais.total,
          ]);
        });
      }

      const wsResumo = XLSX.utils.aoa_to_sheet(resumoData);

      // Estilizar título do resumo
      wsResumo["A1"].s = {
        font: { bold: true, sz: 16 },
        alignment: { horizontal: "center" },
      };

      // Mesclar células do título
      wsResumo["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];

      XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo");

      // Salvar arquivo
      XLSX.writeFile(
        wb,
        `relatorio_procedimentos_medico_${moment().format(
          "YYYY-MM-DD_HHmmss"
        )}.xlsx`
      );

      toast.success("Relatório exportado com sucesso!");
    } catch (err) {
      toast.error("Erro ao exportar relatório");
    } finally {
      stopLoading();
    }
  };

  // Funções auxiliares
  const getTipoExecucaoLabel = (tipo) => {
    const found = tiposExecucao.find((t) => t.value === tipo);
    return found ? found.label : tipo;
  };

  const getTipoExecucaoColor = (tipo) => {
    const found = tiposExecucao.find((t) => t.value === tipo);
    return found ? found.color : "default";
  };

  const calcularResumo = (procedimentos) => {
    if (!procedimentos) return { total: 0, por_tipo: {} };

    const resumo = {
      total: procedimentos.length,
      por_tipo: {},
    };

    procedimentos.forEach((p) => {
      if (!resumo.por_tipo[p.tipo_execucao]) {
        resumo.por_tipo[p.tipo_execucao] = 0;
      }
      resumo.por_tipo[p.tipo_execucao]++;
    });

    return resumo;
  };

  // Filtrar dados localmente
  const dadosFiltrados = useMemo(() => {
    if (!dados) return null;

    let procedimentosFiltrados = [];

    // Extrair todos os procedimentos
    if (Array.isArray(dados)) {
      procedimentosFiltrados = dados.flatMap((m) => m.procedimentos);
    } else if (dados.procedimentos) {
      procedimentosFiltrados = dados.procedimentos;
    }

    // Aplicar filtros
    if (filtros.tipoExecucao.length > 0) {
      procedimentosFiltrados = procedimentosFiltrados.filter((p) =>
        filtros.tipoExecucao.includes(p.tipo_execucao)
      );
    }

    if (filtros.pesquisa) {
      const termo = filtros.pesquisa.toLowerCase();
      procedimentosFiltrados = procedimentosFiltrados.filter(
        (p) =>
          p.cliente?.nome?.toLowerCase().includes(termo) ||
          p.tratamento?.designacao?.toLowerCase().includes(termo) ||
          p.medico?.name?.toLowerCase().includes(termo) ||
          p.evolucao?.toLowerCase().includes(termo) ||
          p.relatorio?.toLowerCase().includes(termo)
      );
    }

    return procedimentosFiltrados;
  }, [dados, filtros]);

  // Paginação local
  const paginatedData = useMemo(() => {
    if (!dadosFiltrados) return [];
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return dadosFiltrados.slice(start, end);
  }, [dadosFiltrados, page, rowsPerPage]);

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMedicoToggle = (medicoId) => {
    setFiltros((prev) => ({
      ...prev,
      medicos: prev.medicos.includes(medicoId)
        ? prev.medicos.filter((id) => id !== medicoId)
        : [...prev.medicos, medicoId],
    }));
  };

  const handleTipoToggle = (tipo) => {
    setFiltros((prev) => ({
      ...prev,
      tipoExecucao: prev.tipoExecucao.includes(tipo)
        ? prev.tipoExecucao.filter((t) => t !== tipo)
        : [...prev.tipoExecucao, tipo],
    }));
  };

  // Calcular totais para os cards
  const totais = useMemo(() => {
    if (!dadosFiltrados)
      return { total: 0, sessoes: 0, isolados: 0, evolucoes: 0 };

    return {
      total: dadosFiltrados.length,
      sessoes: dadosFiltrados.filter(
        (p) => p.tipo_execucao === "SESSAO_PLANO_TRATAMENTO"
      ).length,
      isolados: dadosFiltrados.filter(
        (p) => p.tipo_execucao === "TRATAMENTO_ISOLADO"
      ).length,
      evolucoes: dadosFiltrados.filter(
        (p) => p.tipo_execucao === "EVOLUCAO_AGENDAMENTO_TRANSICAO"
      ).length,
    };
  }, [dadosFiltrados]);

  return (
    <div className="relatorio-procedimentos-medico">
      <div className="relatorio-header">
        <h2>Relatório de Procedimentos Executados por Médico</h2>
        <div className="relatorio-actions">
          <button
            className="btn-icon"
            onClick={() => setShowFiltros(!showFiltros)}
            title="Mostrar/Ocultar Filtros"
          >
            <i className="fas fa-filter"></i>
          </button>
          {dados && (
            <button className="btn-export" onClick={exportarRelatorio}>
              <i className="fas fa-file-excel"></i> Exportar Excel
            </button>
          )}
        </div>
      </div>

      {/* Filtros */}
      {showFiltros && (
        <div className="filtros-container">
          <h3>Filtros Avançados</h3>
          <div className="filtros-grid">
            <div className="filtro-grupo">
              <label>Médicos</label>
              <div className="checkbox-group">
                {medicos.map((medico) => (
                  <label key={medico._id} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={filtros.medicos.includes(medico._id)}
                      onChange={() => handleMedicoToggle(medico._id)}
                    />
                    <span>{medico.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="filtro-grupo">
              <label>Tipo de Execução</label>
              <div className="checkbox-group">
                {tiposExecucao.map((tipo) => (
                  <label key={tipo.value} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={filtros.tipoExecucao.includes(tipo.value)}
                      onChange={() => handleTipoToggle(tipo.value)}
                    />
                    <span className={`badge badge-${tipo.color}`}>
                      {tipo.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cards de Resumo */}
      {dados && (
        <div className="resumo-cards">
          <div className="resumo-card">
            <div className="card-header">
              <span className="card-title">Total de Procedimentos</span>
              <i className="fas fa-procedures"></i>
            </div>
            <div className="card-value">{totais.total}</div>
          </div>

          <div className="resumo-card">
            <div className="card-header">
              <span className="card-title">Sessões de Plano</span>
              <i className="fas fa-clipboard-list"></i>
            </div>
            <div className="card-value primary">{totais.sessoes}</div>
          </div>

          <div className="resumo-card">
            <div className="card-header">
              <span className="card-title">Tratamentos Isolados</span>
              <i className="fas fa-notes-medical"></i>
            </div>
            <div className="card-value secondary">{totais.isolados}</div>
          </div>

          <div className="resumo-card">
            <div className="card-header">
              <span className="card-title">Evoluções</span>
              <i className="fas fa-user-md"></i>
            </div>
            <div className="card-value warning">{totais.evolucoes}</div>
          </div>
        </div>
      )}

      {/* Resumo por Médico */}
      {dados && Array.isArray(dados) && (
        <div className="resumo-medicos">
          <h3>Resumo por Médico</h3>
          <div className="medicos-grid">
            {dados.map((medicoData, index) => (
              <div key={index} className="medico-card">
                <h4>{medicoData.medico?.name || "Médico não identificado"}</h4>
                <div className="medico-stats">
                  <p>
                    Total: <strong>{medicoData.totais.total}</strong>
                  </p>
                  <p>Sessões: {medicoData.totais.sessoes_plano}</p>
                  <p>Isolados: {medicoData.totais.tratamentos_isolados}</p>
                  <p>Evoluções: {medicoData.totais.evolucoes}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mensagens de Feedback */}
      {error && (
        <div className="alert alert-error">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      {/* Pesquisa */}
      {dados && (
        <div className="pesquisa-container">
          <input
            type="text"
            className="pesquisa-input"
            placeholder="Pesquisar por cliente, tratamento, médico ou observações..."
            value={filtros.pesquisa}
            onChange={(e) =>
              setFiltros((prev) => ({ ...prev, pesquisa: e.target.value }))
            }
          />
          <i className="fas fa-search pesquisa-icon"></i>
        </div>
      )}

      {/* Loading */}
      {!dados && !error && (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Carregando relatório...</p>
        </div>
      )}

      {/* Tabela de Dados */}
      {dadosFiltrados && (
        <>
          <div className="tabela-container">
            <table className="tabela-relatorio">
              <thead>
                <tr>
                  <th>Médico</th>
                  <th>Tipo</th>
                  <th>Cliente</th>
                  <th>Tratamento</th>
                  <th>Dente/Faces</th>
                  <th>Data Execução</th>
                  <th>Observações</th>
                  <th>Origem</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((proc, index) => (
                  <tr key={`${proc.tipo_execucao}-${index}`}>
                    <td>{proc.medico?.name || "N/A"}</td>
                    <td>
                      <span
                        className={`badge badge-${getTipoExecucaoColor(
                          proc.tipo_execucao
                        )}`}
                      >
                        {getTipoExecucaoLabel(proc.tipo_execucao)}
                      </span>
                    </td>
                    <td>
                      <Link
                        to={`/clientes/${proc.cliente._id}`}
                        className="tooltip__client-link"
                      >
                        {proc.cliente?.nome || "N/A"}
                      </Link>
                    </td>
                    <td>{proc.tratamento?.designacao || "N/A"}</td>
                    <td>
                      {proc.dente || "-"}
                      {proc.faces && proc.faces.length > 0 && (
                        <small className="faces-info">
                          Faces: {proc.faces.join(", ")}
                        </small>
                      )}
                    </td>
                    <td>
                      {proc.data_inicio && (
                        <div>
                          <div>
                            {moment(proc.data_inicio).format("DD/MM/YYYY")}
                          </div>
                          <small>
                            {moment(proc.data_inicio).format("HH:mm")}
                          </small>
                        </div>
                      )}
                    </td>
                    <td className="td-observacoes">
                      <div className="observacoes-text">
                        {proc.evolucao || proc.relatorio || "-"}
                      </div>
                    </td>
                    <td>
                      {proc.numero_plano && `Plano: ${proc.numero_plano}`}
                      {proc.numero && `Isolado: ${proc.numero}`}
                      {!proc.numero_plano && !proc.numero && "Evolução"}
                    </td>
                  </tr>
                ))}
                {paginatedData.length === 0 && (
                  <tr>
                    <td colSpan="8" className="empty-message">
                      Nenhum procedimento encontrado com os filtros aplicados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          <div className="paginacao-container">
            <div className="paginacao-info">
              Mostrando {page * rowsPerPage + 1} -{" "}
              {Math.min((page + 1) * rowsPerPage, dadosFiltrados.length)} de{" "}
              {dadosFiltrados.length} registros
            </div>
            <div className="paginacao-controles">
              <select
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                className="rows-per-page"
              >
                <option value={10}>10 por página</option>
                <option value={25}>25 por página</option>
                <option value={50}>50 por página</option>
                <option value={100}>100 por página</option>
              </select>
              <button
                onClick={() => handleChangePage(0)}
                disabled={page === 0}
                className="btn-paginacao"
              >
                <i className="fas fa-angle-double-left"></i>
              </button>
              <button
                onClick={() => handleChangePage(page - 1)}
                disabled={page === 0}
                className="btn-paginacao"
              >
                <i className="fas fa-angle-left"></i>
              </button>
              <span className="pagina-atual">
                Página {page + 1} de{" "}
                {Math.ceil(dadosFiltrados.length / rowsPerPage)}
              </span>
              <button
                onClick={() => handleChangePage(page + 1)}
                disabled={
                  page >= Math.ceil(dadosFiltrados.length / rowsPerPage) - 1
                }
                className="btn-paginacao"
              >
                <i className="fas fa-angle-right"></i>
              </button>
              <button
                onClick={() =>
                  handleChangePage(
                    Math.ceil(dadosFiltrados.length / rowsPerPage) - 1
                  )
                }
                disabled={
                  page >= Math.ceil(dadosFiltrados.length / rowsPerPage) - 1
                }
                className="btn-paginacao"
              >
                <i className="fas fa-angle-double-right"></i>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default RelatorioProcedimentosMedico;
